import React from 'react';
import ModalStateType from 'models/generic/ModalState';
import axios from 'helpers/axios';
import config from 'helpers/config';
import Modal from 'components/common/Modal';
import { Divider, Form, Transition } from 'semantic-ui-react';
import User from 'models/UserModel';
import AccessPointTypes from 'models/types/AccessPointTypes';
import AccessPointsTab from './AccessPointsTab';
import Toggle from 'components/common/Toggle';
import { connect } from 'react-redux';

interface UserAccessPoint {
    accessPoint: AccessPointTypes;
    accessLevel: number;
    isActive: boolean;
}

interface Props {
    userId?: string;
    onClose: (userCreated?: boolean, userEdited?: boolean) => void;
    currentUser: User;
}

interface ComponentState {
    modalState: ModalStateType;
    modalHeader: string;
    user: User;
    isLoading: boolean;
    isFormExpanded: boolean;
    isImagesModalOpen: boolean;
    formErrors: any;

    userAccessPoints: UserAccessPoint[];
    activeTab: number;
}

const requiredFields = ['firstName', 'lastName', 'email'];

class UserModal extends React.Component<Props, ComponentState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            modalState: this.props.userId ? ModalStateType.Edit : ModalStateType.Create,
            modalHeader: '',
            isLoading: true,
            isFormExpanded: true,
            user: {} as User,
            formErrors: {},
            isImagesModalOpen: false,

            userAccessPoints: [],
            activeTab: 0,
        };

        this.getUser = this.getUser.bind(this);
        this.setModalHeader = this.setModalHeader.bind(this);
        this.getValue = this.getValue.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.save = this.save.bind(this);
        this.toggleImagesModal = this.toggleImagesModal.bind(this);
        this.handleAccessPointChange = this.handleAccessPointChange.bind(this);
        this.handleBulkAccessPointChange = this.handleBulkAccessPointChange.bind(this);
    }
    componentDidMount() {
        if (this.state.modalState === ModalStateType.Edit) {
            this.setState({ isLoading: true });
            this.getUser();
        } else {
            this.initializeNewUserAccessPoints();
            this.setModalHeader();
            this.setState({ isLoading: false });
        }
    }

    handleChange(event: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>) {
        const { name, value } = event.currentTarget;

        const updatedUser: User = { ...this.state.user, [name]: value };
        this.setState({ user: updatedUser });
    }

    initializeNewUserAccessPoints() {
        // Initialize with default access points for new users (all disabled)
        const defaultAccessPoints: UserAccessPoint[] = [
            { accessPoint: AccessPointTypes.Dashboard, accessLevel: 1, isActive: false },
            { accessPoint: AccessPointTypes.Calendar, accessLevel: 1, isActive: false },
            { accessPoint: AccessPointTypes.Inventory, accessLevel: 1, isActive: false },
            { accessPoint: AccessPointTypes.Reports, accessLevel: 1, isActive: false },
            { accessPoint: AccessPointTypes.Settings, accessLevel: 1, isActive: false },
        ];
        this.setState({ userAccessPoints: defaultAccessPoints });
    }
    handleAccessPointChange(accessPoint: AccessPointTypes, isActive: boolean) {
        const { userAccessPoints } = this.state;
        const updatedAccessPoints = userAccessPoints.map((ap) =>
            ap.accessPoint === accessPoint ? { ...ap, isActive } : ap,
        );

        // If this access point doesn't exist, add it
        if (!userAccessPoints.find((ap) => ap.accessPoint === accessPoint)) {
            updatedAccessPoints.push({ accessPoint, accessLevel: 1, isActive });
        }

        this.setState({ userAccessPoints: updatedAccessPoints });
    }

    handleBulkAccessPointChange(newAccessPoints: UserAccessPoint[]) {
        // Merge with existing access points, updating all provided ones
        const { userAccessPoints } = this.state;

        // Create a map of all access point types
        const allAccessPointTypes = [
            AccessPointTypes.Dashboard,
            AccessPointTypes.Calendar,
            AccessPointTypes.Inventory,
            AccessPointTypes.Reports,
            AccessPointTypes.Settings,
        ];

        // Create updated access points array
        const updatedAccessPoints = allAccessPointTypes.map((apType) => {
            const newAccessPoint = newAccessPoints.find((nap) => nap.accessPoint === apType);
            const existingAccessPoint = userAccessPoints.find((uap) => uap.accessPoint === apType);

            // Use new access point if provided, otherwise keep existing or create default
            return (
                newAccessPoint ||
                existingAccessPoint || {
                    accessPoint: apType,
                    accessLevel: 1,
                    isActive: false,
                }
            );
        });

        this.setState({ userAccessPoints: updatedAccessPoints });
    }

    getAllAccessPoints() {
        // This method is no longer needed with the simplified approach
        this.setState({ isLoading: false });
        this.setModalHeader();
    }
    getUser() {
        axios
            .get(`${config.apiUrl}/user/${this.props.userId}`)
            .then((response) => {
                this.setState({ user: response.data, isLoading: false });
                this.setModalHeader();
            })
            .catch((err) => console.log(err));

        axios
            .get(`${config.apiUrl}/access/${this.props.userId}`)
            .then((response) => {
                // Transform the response to match our simplified structure
                const accessPoints = response.data.map((ap: any) => ({
                    accessPoint: ap.accessPoint,
                    accessLevel: ap.accessLevel,
                    isActive: ap.isActive,
                }));
                this.setState({ userAccessPoints: accessPoints });
            })
            .catch((err) => {
                console.log(err);
                // If no access points found, initialize with defaults
                this.initializeNewUserAccessPoints();
            });
    }

    setModalHeader() {
        if (this.state.modalState === ModalStateType.Edit) {
            const { firstName, lastName } = this.state.user!;
            const modalHeader = `Edit ${firstName} ${lastName}`;
            this.setState({ modalHeader });
        } else {
            this.setState({ modalHeader: 'Register new user' });
        }
    }

    getValue(name: string) {
        // @ts-ignore
        return this.state.user[name] || '';
    }
    save() {
        if (!this.validateForm()) {
            return;
        }

        this.setState({ isLoading: true });

        const userData = {
            ...this.state.user,
            accessPoints: this.state.userAccessPoints,
        };

        if (this.state.modalState === ModalStateType.Create) {
            this.createUser(userData);
        } else {
            this.updateUser(userData);
        }
    }

    validateForm() {
        const errors: any = {};
        requiredFields.forEach((field) => {
            //@ts-ignore
            if (!this.state?.user?.[field] || this.state?.user?.[field]?.length === 0) {
                errors[field] = 'Cannot be empty';
            }
        });
        this.setState({ formErrors: errors });
        const isFormValid = Object.keys(errors).length === 0;
        return isFormValid;
    }

    updateUser(userData: any) {
        // Update user basic info
        axios
            .put(`${config.apiUrl}/user`, userData)
            .then((_) => {
                // Update access points separately
                if (userData.accessPoints) {
                    return this.updateUserAccessPoints(userData.accessPoints);
                }
            })
            .then((_) => this.props.onClose(false, true))
            .catch((_) => {
                this.setState({ isLoading: false });
            });
    }

    createUser(userData: any) {
        axios
            .post(`${config.apiUrl}/user`, userData)
            .then((response) => {
                // If user creation includes access points, update them
                if (userData.accessPoints && response.data.id) {
                    return this.updateUserAccessPoints(userData.accessPoints, response.data.id);
                }
            })
            .then((_) => {
                this.props.onClose(true, false);
            })
            .catch((_) => {
                this.setState({ isLoading: false });
            });
    }

    updateUserAccessPoints(accessPoints: UserAccessPoint[], userId?: string) {
        const targetUserId = userId || this.state.user.id;

        // Send only the active access points to the server
        const activeAccessPoints = accessPoints.filter((ap) => ap.isActive);

        return axios.post(`${config.apiUrl}/access/bulk-update`, {
            userId: targetUserId,
            accessPoints: activeAccessPoints,
        });
    }

    toggleImagesModal() {
        const { isImagesModalOpen } = this.state;
        this.setState({ isImagesModalOpen: !isImagesModalOpen });
        if (isImagesModalOpen) {
            axios
                .get(`${config.apiUrl}/file/images/`, {
                    params: { relatedEntityType: 'user', relatedEntityId: this.state.user.id },
                })
                .then((res) =>
                    this.setState((prevState) => ({
                        user: {
                            ...prevState.user,
                            files: res.data,
                        },
                    })),
                );
        }
    }
    getTabs() {
        const { currentUser } = this.props;
        const isReadOnly = this.state.user.id === currentUser.id; // Don't allow users to edit their own permissions

        return [
            {
                title: 'Access Points',
                component: (
                    <AccessPointsTab
                        userAccessPoints={this.state.userAccessPoints}
                        onAccessPointChange={this.handleAccessPointChange}
                        onBulkAccessPointChange={this.handleBulkAccessPointChange}
                        isReadOnly={isReadOnly}
                    />
                ),
            },
            {
                title: 'Notifications',
                component: <div className="p-4">Notifications configuration coming soon...</div>,
            },
        ];
    }

    render() {
        const { activeTab, modalHeader, isLoading, isFormExpanded, formErrors, user, modalState } = this.state;
        const { currentUser } = this.props;
        const tabs = this.getTabs();
        return (
            <Modal
                headerText={modalHeader}
                isOpen={true}
                onClose={() => this.props.onClose(false, false)}
                onSave={this.save}
                isLoading={isLoading}
            >
                <div>
                    <Form size="large">
                        <Form.Group unstackable widths={2}>
                            <Form.Input
                                required
                                placeholder="First Name"
                                label="First Name"
                                name="firstName"
                                onChange={this.handleChange}
                                value={this.getValue('firstName')}
                                error={formErrors.firstName}
                            />
                            <Form.Input
                                required={true}
                                placeholder="Last Name"
                                label="Last Name"
                                name="lastName"
                                onChange={this.handleChange}
                                value={this.getValue('lastName')}
                                error={formErrors.lastName}
                            />
                        </Form.Group>
                        <Form.Group unstackable widths={2}>
                            <Form.Input
                                required={true}
                                placeholder="Email"
                                label="Email"
                                name="email"
                                onChange={this.handleChange}
                                error={formErrors.email}
                                value={this.getValue('email')}
                            />

                            <Form.Input
                                placeholder="Phone Number"
                                label="Phone Number"
                                name="phoneNumber"
                                onChange={this.handleChange}
                                value={this.getValue('phoneNumber')}
                            />
                        </Form.Group>

                        {modalState !== ModalStateType.Create && user.id !== currentUser.id && (
                            <Form.Group unstackable widths={2}>
                                <div className="ml-3.5">
                                    <div className="font-semibold">
                                        <span>Is Active</span>
                                    </div>
                                    <Toggle
                                        onChange={(newValue) =>
                                            this.setState({
                                                user: {
                                                    ...this.state.user,
                                                    isActive: newValue,
                                                },
                                            })
                                        }
                                        value={user.isActive}
                                        name="isUserActive"
                                        disabled={false}
                                    />
                                </div>
                            </Form.Group>
                        )}
                        <Divider horizontal>
                            <span
                                className="clickable expand-link"
                                onClick={() => this.setState({ isFormExpanded: !isFormExpanded })}
                            >
                                {isFormExpanded ? 'Less' : 'More'}
                            </span>
                        </Divider>
                        <Transition.Group animation="fade up" duration="200">
                            {isFormExpanded && (
                                <>
                                    <Form.Group>
                                        <Form.TextArea
                                            width="16"
                                            placeholder="Notes"
                                            label="Notes"
                                            name="notes"
                                            onChange={this.handleChange}
                                            value={this.getValue('notes')}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </Transition.Group>
                    </Form>
                    {!isLoading &&
                        tabs.map((tab, index) => {
                            return (
                                <div
                                    className={`inline-flex px-5 border-b-4 ${
                                        activeTab === index
                                            ? ' border-blue-500 '
                                            : '  border-gray-500 hover:text-blue-400'
                                    }`}
                                    key={index}
                                >
                                    <span
                                        className={`clickable text-lg ${
                                            activeTab === index
                                                ? 'font-bold text-blue-500 border-blue-500 '
                                                : ' font-medium text-gray-400 border-gray-500 hover:text-blue-400'
                                        }`}
                                        onClick={() => this.setState({ activeTab: index })}
                                    >
                                        {tab.title}
                                    </span>
                                </div>
                            );
                        })}
                    {tabs[activeTab].component}
                </div>
            </Modal>
        );
    }
}
const mapStateToProps = (state: any) => ({
    currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(UserModal);
